import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { Button } from '../../components/buttons/Button';
import { User, Briefcase, Home as HomeIcon, ShieldCheck, DollarSign, CheckCircle, Star, MapPin } from 'lucide-react';
import { PropertyCard } from '../../components/cards/PropertyCard';
import { StudentVoucherAd } from '../../components/ads/StudentVoucherAd';
import { GoogleGenAI } from "@google/genai";
import { SEOHead } from '../../components/seo/SEOHead';
import { generateOrganizationSchema } from '../../utils/seo/structuredData';

const HERO_PROMPT = `This image captures a vibrant and diverse group of five young adults walking together on a sunny day, with a large digital billboard advertising student housing prominently displayed in the upper part of the frame. In the foreground, from left to right: * A young East Asian woman with long, dark hair is smiling and looking towards the man next to her. She is wearing a light grey knit sweater and blue jeans. * A young Black man with short, curly hair is next, also smiling broadly and looking in the same direction. He has his arm linked with the woman in the middle. He wears a black denim jacket over a white t-shirt and dark trousers. A watch is visible on his left wrist. * In the center is a young woman wearing a black hijab and a long-sleeved black garment (possibly an abaya or dress). She is laughing joyfully and has her arms linked with both the man to her left and the man to her right. * Next is a young White man with short, light brown or reddish hair and clear-framed glasses. He is smiling widely, looking towards the woman on his right, and is wearing a light blue denim shirt unbuttoned over a white t-shirt, paired with blue jeans. His arm is linked with the woman in the middle. * On the far right, a young Black woman with long, braided hair is smiling and laughing, looking towards the man next to her. She is wearing a black t-shirt, a light beige or cream-colored open cardigan, and blue jeans. A delicate necklace is visible around her neck, and a black bag strap crosses her shoulder. The group appears to be walking on a paved pathway or plaza, engaged in cheerful conversation, conveying a strong sense of friendship and camaraderie. Above the group, dominating the upper middle and right portion of the image, is a large, dark-framed digital billboard or screen. The screen displays a vibrant gradient background, transitioning from a bright teal-green on the left to a deep blue-purple on the right. White text on the screen reads: * In a large, bold font: "Find Your Perfect Student Home" * Below it, in a smaller font: "Connect directly with verified landlords and agents. Secure, transparent, and built specifically for students." To the right of the main headline, there is a stylized graphic of a house formed by interconnected lines and dots, suggesting a digital network or platform. In the background, behind the group and the billboard, there is a modern, multi-story building with a dark facade and numerous windows, possibly part of a university campus or urban complex. Neatly trimmed, spherical green trees or bushes line the pathway, adding a touch of greenery to the urban setting. The scene is brightly lit by natural sunlight, casting a warm glow and suggesting a clear, sunny day.`;

const SA_INSTITUTIONS = [
  "University of Cape Town", "Wits University", "Stellenbosch University", "University of Pretoria",
  "University of Johannesburg", "UKZN", "Rhodes University", "UWC", "North-West University",
  "Tshwane University of Technology", "CPUT", "Durban University of Technology",
  "Majuba TVET College", "False Bay TVET", "Goldfields TVET", "Vaal University of Technology",
  "Nelson Mandela University", "University of Limpopo", "Walter Sisulu University"
];

export const Home: React.FC = () => {
  const { properties } = useData();
  const featuredProperties = properties.slice(0, 4);
  const [heroImage, setHeroImage] = useState<string>("https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80");

  useEffect(() => {
    const generateHeroImage = async () => {
      // 1. Check Cache
      const cachedImage = localStorage.getItem('sored_hero_image_v1');
      if (cachedImage) {
        setHeroImage(cachedImage);
        return;
      }

      // 2. Generate if not cached
      try {
        const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
        const response = await ai.models.generateContent({
          model: 'gemini-2.5-flash-image',
          contents: {
            parts: [{ text: HERO_PROMPT }],
          },
          config: {
            imageConfig: {
              aspectRatio: "4:3",
            }
          }
        });

        // 3. Extract and Save
        if (response.candidates?.[0]?.content?.parts) {
          for (const part of response.candidates[0].content.parts) {
            if (part.inlineData) {
              const base64 = part.inlineData.data;
              const mime = part.inlineData.mimeType || 'image/png';
              const dataUrl = `data:${mime};base64,${base64}`;

              setHeroImage(dataUrl);

              try {
                localStorage.setItem('sored_hero_image_v1', dataUrl);
              } catch (storageErr) {
                console.warn("Failed to cache hero image (likely storage quota exceeded).", storageErr);
              }
              break;
            }
          }
        }
      } catch (error) {
        console.error("Failed to generate hero image:", error);
      }
    };

    generateHeroImage();
  }, []);

  const orgSchema = generateOrganizationSchema();

  return (
    <div className="flex flex-col min-h-screen font-sans">
      <SEOHead
        title="Find Affordable Rooms for Rent in South Africa | IndigoBeans Rooms"
        description="Search and book verified, affordable rooms for rent across South Africa. Browse student accommodation and rooms by location, price, and amenities."
        keywords={['rooms for rent', 'student accommodation', 'affordable rooms', 'Pretoria rooms', 'Johannesburg rooms', 'Cape Town rooms']}
        canonical="https://rooms.indigobeans.co.za/"
        schema={[orgSchema]}
      />

      {/* Helper to pre-load location data for SEO consistency */}
      <div className="hidden">
        <Link to="/rooms/pretoria">Pretoria Rooms</Link>
        <Link to="/rooms/johannesburg">Johannesburg Rooms</Link>
        <Link to="/rooms/cape-town">Cape Town Rooms</Link>
        <Link to="/rooms/durban">Durban Rooms</Link>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 60s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* 2. Hero Section */}
      <div className="relative bg-[#2f5c96]/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col-reverse lg:flex-row items-center py-16 lg:py-24 gap-12">

            {/* Left Column: Headline & CTA */}
            <div className="flex-1 text-center lg:text-left z-10">
              <h1 className="text-4xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight mb-6">
                Find Your Perfect <br />
                <span className="text-[#2f5c96]">Student Home</span>
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Connect directly with verified landlords and agents. Secure, transparent, and built specifically for students.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/browse?status=available">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8 shadow-lg shadow-[#2f5c96]/20">Find Accommodation</Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="w-full sm:w-auto text-lg px-8">List a Property</Button>
                </Link>
              </div>
            </div>

            {/* Right Column: Visual Block */}
            <div className="flex-1 w-full max-w-lg lg:max-w-none">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                <img
                  src={heroImage}
                  alt="Students gathering under digital billboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#2f5c96]/20 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Quick Search / Filter Bar */}
      <div className="-mt-8 z-20 px-4">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-xl border border-gray-100 p-4 md:p-6 flex flex-col md:flex-row gap-4 items-center">
          <div className="flex-1 w-full relative">
            <MapPin className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="City, University, or Area"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none transition-all"
            />
          </div>
          <div className="flex-1 w-full relative">
            <DollarSign className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
            <select className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none appearance-none transition-all text-gray-600">
              <option>Price Range</option>
              <option>R0 - R4000</option>
              <option>R4000 - R8000</option>
              <option>R8000+</option>
            </select>
          </div>
          <div className="flex-1 w-full relative">
            <HomeIcon className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
            <select className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2f5c96] focus:border-transparent outline-none appearance-none transition-all text-gray-600">
              <option>Room Type</option>
              <option>Studio</option>
              <option>Shared House</option>
              <option>Dorm</option>
            </select>
          </div>
          <Link to="/browse?status=available" className="w-full md:w-auto">
            <Button className="w-full py-3 px-8 bg-[#2f5c96] hover:bg-[#c19cca]">Search</Button>
          </Link>
        </div>
      </div>

      {/* 4. Featured Accommodation Listings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Featured Accommodation</h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Top-rated student homes carefully selected for comfort, location, and value.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProperties.map(p => <PropertyCard key={p.id} property={p} />)}
          </div>
          <div className="mt-12 text-center">
            <Link to="/browse?status=available">
              <Button variant="outline" className="px-8">View All Listings</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. Student Voucher Ad Section - NEW */}
      <StudentVoucherAd />

      {/* 6. How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">How Indigo Beans Rooms Works</h2>
            <div className="w-24 h-1 bg-[#2f5c96] mx-auto mt-4 rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-12 text-center relative">
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gray-200 -z-0"></div>
            <div className="relative z-10 bg-gray-50">
              <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-[#2f5c96]/10">
                <User className="w-10 h-10 text-[#2f5c96]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">For Students</h3>
              <p className="text-gray-600 leading-relaxed px-4">Browse verified listings, connect with hosts, and book your ideal home securely online.</p>
            </div>
            <div className="relative z-10 bg-gray-50">
              <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-[#2f5c96]/10">
                <Briefcase className="w-10 h-10 text-[#2f5c96]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">For Agents</h3>
              <p className="text-gray-600 leading-relaxed px-4">List multiple properties, manage inquiries efficiently, and grow your student portfolio.</p>
            </div>
            <div className="relative z-10 bg-gray-50">
              <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6 border-4 border-[#2f5c96]/10">
                <HomeIcon className="w-10 h-10 text-[#2f5c96]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">For Landlords</h3>
              <p className="text-gray-600 leading-relaxed px-4">Directly list your property, screen tenants, and enjoy guaranteed rent payments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Why SORED / Value Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-12">Why Choose Indigo Beans Rooms?</h2>
          <div className="grid sm:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl border border-gray-100 hover:border-[#c19cca]/30 hover:shadow-lg transition-all text-left flex items-start gap-4">
              <ShieldCheck className="w-8 h-8 text-[#c19cca] flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg mb-1">Verified Listings</h4>
                <p className="text-sm text-gray-600">Every home is checked to ensure it meets our safety and quality standards.</p>
              </div>
            </div>
            <div className="p-6 rounded-xl border border-gray-100 hover:border-[#c19cca]/30 hover:shadow-lg transition-all text-left flex items-start gap-4">
              <DollarSign className="w-8 h-8 text-[#c19cca] flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg mb-1">No Hidden Fees</h4>
                <p className="text-sm text-gray-600">Transparent pricing for everyone. What you see is exactly what you pay.</p>
              </div>
            </div>
            <div className="p-6 rounded-xl border border-gray-100 hover:border-[#c19cca]/30 hover:shadow-lg transition-all text-left flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-[#c19cca] flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg mb-1">Secure Booking</h4>
                <p className="text-sm text-gray-600">Payments are protected until you move in. Book with total confidence.</p>
              </div>
            </div>
            <div className="p-6 rounded-xl border border-gray-100 hover:border-[#c19cca]/30 hover:shadow-lg transition-all text-left flex items-start gap-4">
              <User className="w-8 h-8 text-[#c19cca] flex-shrink-0" />
              <div>
                <h4 className="font-bold text-lg mb-1">24/7 Support</h4>
                <p className="text-sm text-gray-600">Our dedicated team is always here to help you settle in.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Call-to-Action Split Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/browse?status=available" className="group relative overflow-hidden rounded-2xl h-80 flex items-center justify-center text-center bg-[#2f5c96] hover:bg-[#254a7a] transition-colors">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1556056504-5c7696c4c28d?auto=format&fit=crop&q=80')] opacity-20 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"></div>
              <div className="relative z-10 p-8">
                <h3 className="text-3xl font-bold text-white mb-4">Find Accommodation</h3>
                <p className="text-blue-100 mb-8 max-w-sm mx-auto">Discover thousands of student rooms, flats, and studios.</p>
                <span className="inline-block px-8 py-3 bg-white text-[#2f5c96] font-bold rounded-lg group-hover:bg-[#c19cca] group-hover:text-white transition-colors">Start Searching</span>
              </div>
            </Link>
            <Link to="/register" className="group relative overflow-hidden rounded-2xl h-80 flex items-center justify-center text-center bg-[#2f5c96] hover:bg-[#254a7a] transition-colors">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80')] opacity-20 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"></div>
              <div className="relative z-10 p-8">
                <h3 className="text-3xl font-bold text-white mb-4">List a Property</h3>
                <p className="text-blue-100 mb-8 max-w-sm mx-auto">Reach thousands of students and manage your rentals easily.</p>
                <span className="inline-block px-8 py-3 bg-white text-[#2f5c96] font-bold rounded-lg group-hover:bg-[#c19cca] group-hover:text-white transition-colors">Get Started</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. Trust & Credibility Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-8">Trusted by Students From</p>

          {/* Scrolling Marquee */}
          <div className="relative w-full overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap gap-16 w-max">
              {[...SA_INSTITUTIONS, ...SA_INSTITUTIONS].map((inst, i) => (
                <span key={i} className="text-lg md:text-2xl font-bold text-gray-300 hover:text-gray-500 transition-colors cursor-default">
                  {inst}
                </span>
              ))}
            </div>
            <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none"></div>
            <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none"></div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-center mb-4">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
              </div>
              <p className="text-gray-600 italic mb-4">"Found my dream studio in Hatfield in just 2 days. The process was so smooth and the landlord was super responsive."</p>
              <div className="mt-auto">
                <p className="font-bold text-gray-900">Thabo Molefe</p>
                <p className="text-xs text-gray-500">Student, University of Pretoria</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-center mb-4">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
              </div>
              <p className="text-gray-600 italic mb-4">"As a landlord in Stellies, Indigo Beans Rooms has made filling my rooms incredibly easy. The dashboard is fantastic."</p>
              <div className="mt-auto">
                <p className="font-bold text-gray-900">Johan van der Merwe</p>
                <p className="text-xs text-gray-500">Property Manager, Stellenbosch</p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-center mb-4">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />)}
              </div>
              <p className="text-gray-600 italic mb-4">"Secure payments gave my parents in KZN peace of mind. Highly recommend to any first-year student."</p>
              <div className="mt-auto">
                <p className="font-bold text-gray-900">Noluthando Z.</p>
                <p className="text-xs text-gray-500">Engineering Student, Wits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};